import { enqueueSnackbar } from 'notistack';
import { useRef, useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';
import { WordRepeat, WordLayoutPart, WordReplacement, WordTemplateCheck, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';
import {
  useApplyWordPlaceholdersMutation,
  useGetWordTemplateLayoutMutation,
  useSuggestWordPlaceholdersMutation,
  useGetWordTemplatePlaceholdersQuery,
} from 'src/services/presentations/presentations.service';

import Iconify from 'src/components/iconify';

import { withReason } from '../hooks/api-error-reason';
import { segments, repeatOf, coveredBy, bodyParagraphs, previewChanges, placeholderText } from './word-placeholder-editor.utils';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  fileId: string | null;
  name: string;
  aiAvailable: boolean;
  onClose: VoidFunction;
  /** The file with the changes written in, as a new upload with its check. */
  onApplied: (result: { file_id: string; applied: number; check: WordTemplateCheck }) => void;
}

type Picker =
  | { kind: 'text'; find: string; part: string; index: number; top: number; left: number }
  | { kind: 'paragraphs'; from: number; to: number; top: number; left: number };

const EMPTY: WordPlaceholderChanges = { replacements: [], repeats: [] };
const EXTRA_FIELDS = new Set(['candidate.summary', 'document.confidentiality']);

/** The placeholders to choose from: single values by group, and each list's fields for repeats. */
function placeholderOptions(vocabulary: { tag: string; repeats: boolean }[]) {
  const groups = new Map<string, string[]>();
  const lists = new Map<string, string[]>();
  vocabulary.forEach(({ tag, repeats }) => {
    const [list, field] = tag.split('[].');
    if (repeats) lists.set(tag, lists.get(tag) ?? []);
    else if (field) lists.set(list, [...(lists.get(list) ?? []), `${list}.${field}`]);
    else if (!tag.startsWith('has.')) groups.set(tag.split('.')[0], [...(groups.get(tag.split('.')[0]) ?? []), tag]);
  });
  // A list of plain items, such as skills, is placed as itself.
  lists.forEach((fields, list) => {
    if (!fields.length) lists.set(list, [list]);
  });
  return { groups: Array.from(groups.entries()), lists };
}

/**
 * The placeholder editor (staging feedback, JT1-1887). The agency's Word file is shown as text: the
 * admin selects text such as "[insert candidate's name]" and picks the placeholder that replaces it,
 * everywhere it appears, and marks the table rows or paragraphs that repeat for each role. Nothing
 * changes until Apply, which writes the choices into a copy of the file.
 */
export default function WordPlaceholderEditor({ open, fileId, name, aiAvailable, onClose, onApplied }: Props) {
  const { t } = useTranslate();
  const [loadLayout, { isLoading: loading }] = useGetWordTemplateLayoutMutation();
  const [apply, { isLoading: applying }] = useApplyWordPlaceholdersMutation();
  const [suggest, { isLoading: suggesting }] = useSuggestWordPlaceholdersMutation();
  const { currentData: vocabulary } = useGetWordTemplatePlaceholdersQuery();
  const [parts, setParts] = useState<WordLayoutPart[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [changes, setChanges] = useState<WordPlaceholderChanges>(EMPTY);
  const [picker, setPicker] = useState<Picker | null>(null);
  const [search, setSearch] = useState('');
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; table: number; row: number } | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const page = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !fileId) return;
    setParts(null);
    setFailed(false);
    setChanges(EMPTY);
    setNotes([]);
    loadLayout({ file_id: fileId }).unwrap().then((result) => setParts(result.parts)).catch(() => setFailed(true));
  }, [open, fileId, loadLayout]);

  const where = useMemo(() => bodyParagraphs(parts ?? []), [parts]);
  const preview = useMemo(() => previewChanges(parts ?? [], changes), [parts, changes]);
  const options = useMemo(() => placeholderOptions(vocabulary ?? []), [vocabulary]);
  const original = useMemo(() => {
    const texts = new Map<number, string>();
    parts?.find((p) => p.kind === 'body')?.items.forEach((item) => {
      if (item.type === 'paragraph') texts.set(item.index, item.text);
    });
    return texts;
  }, [parts]);

  const label = (path: string) => {
    if (options.lists.has(path)) return t(`presentations.word.editor.list_fields.${path}`);
    const [group, field] = path.split('.');
    if (options.lists.has(group)) return t(`presentations.word.editor.list_fields.${group}.${field}`);
    return t(EXTRA_FIELDS.has(path) ? `presentations.word.editor.extra_fields.${group}_${field}` : `presentations.fields.keys.${group}_${field}`);
  };

  // ---- Choosing text and placeholders ----

  const onMouseUp = () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const paragraphOf = (node: Node | null) => (node instanceof Element ? node : node?.parentElement ?? null)?.closest<HTMLElement>('[data-paragraph]') ?? null;
    const start = paragraphOf(range.startContainer);
    const reached = paragraphOf(range.endContainer);
    if (!start || !reached) return;
    // A selection that ends at the very start of the next paragraph (a triple click) ends on the one before.
    const all = Array.from(page.current?.querySelectorAll<HTMLElement>('[data-paragraph]') ?? []);
    const end = reached !== start && range.endOffset === 0 ? all[all.indexOf(reached) - 1] ?? reached : reached;
    const rect = range.getBoundingClientRect();
    const at = { top: rect.bottom + 6, left: rect.left };
    setSearch('');

    if (start === end) {
      const find = selection.toString().trim();
      if (!find) return;
      if (/[{}]/.test(find)) {
        enqueueSnackbar(t('presentations.word.editor.has_placeholder'), { variant: 'info' });
        return;
      }
      setPicker({ kind: 'text', find, part: start.dataset.part as string, index: Number(start.dataset.paragraph), ...at });
      return;
    }
    const from = Math.min(Number(start.dataset.paragraph), Number(end.dataset.paragraph));
    const to = Math.max(Number(start.dataset.paragraph), Number(end.dataset.paragraph));
    const outsideTables = start.dataset.part === 'body' && end.dataset.part === 'body' && where.get(from)?.table === undefined && where.get(to)?.table === undefined;
    if (outsideTables) setPicker({ kind: 'paragraphs', from, to, ...at });
    else enqueueSnackbar(t('presentations.word.editor.select_hint'), { variant: 'info' });
  };

  const pickerRepeat = picker?.kind === 'text' ? repeatOf(changes, where, picker.part, picker.index) : -1;

  const choose = (placeholder: string) => {
    if (picker?.kind !== 'text') return;
    const { find } = picker;
    const add = (list: WordReplacement[]) => [...list.filter((c) => c.find !== find), { find, placeholder }];
    setChanges((current) => (pickerRepeat < 0
      ? { ...current, replacements: add(current.replacements) }
      : { ...current, repeats: current.repeats.map((repeat, i) => (i === pickerRepeat ? { ...repeat, replacements: add(repeat.replacements) } : repeat)) }));
    setPicker(null);
    window.getSelection()?.removeAllRanges();
  };

  const addRepeat = (repeat: WordRepeat) => {
    setPicker(null);
    setRowMenu(null);
    window.getSelection()?.removeAllRanges();
    const taken = new Set(changes.repeats.flatMap((r) => coveredBy(r, where)));
    if (coveredBy(repeat, where).some((index) => taken.has(index))) {
      enqueueSnackbar(t('presentations.word.editor.overlaps'), { variant: 'warning' });
      return;
    }
    setChanges((current) => ({ ...current, repeats: [...current.repeats, repeat] }));
  };

  const removeRepeat = (r: number) => setChanges((current) => ({ ...current, repeats: current.repeats.filter((_, i) => i !== r) }));

  const removeReplacement = (r: number, i: number) => setChanges((current) => (r < 0
    ? { ...current, replacements: current.replacements.filter((_, j) => j !== i) }
    : { ...current, repeats: current.repeats.map((repeat, k) => (k === r ? { ...repeat, replacements: repeat.replacements.filter((_, j) => j !== i) } : repeat)) }));

  // ---- AI and applying ----

  const suggestWithAi = async () => {
    if (!fileId) return;
    try {
      const result = await suggest({ file_id: fileId }).unwrap();
      const known = new Set(changes.replacements.map((c) => c.find));
      const added = result.placeholders
        .filter((p) => !known.has(p.find) && !/[{}]/.test(p.find))
        .filter((p, i, all) => all.findIndex((q) => q.find === p.find) === i)
        .map((p) => ({ find: p.find, placeholder: p.placeholder }));
      setChanges((current) => ({ ...current, replacements: [...current.replacements, ...added] }));
      setNotes(result.notes.map((note) => note.advice));
      enqueueSnackbar(added.length ? t('presentations.word.editor.ai_added', { count: added.length }) : t('presentations.word.ai_nothing'), { variant: added.length ? 'success' : 'info' });
    } catch (error) {
      enqueueSnackbar(withReason(t('presentations.ai.failed'), error), { variant: 'error' });
    }
  };

  const save = async () => {
    if (!fileId) return;
    try {
      onApplied(await apply({ file_id: fileId, ...changes }).unwrap());
    } catch (error) {
      const errors = (error as { data?: { context?: { errors?: string[] } } })?.data?.context?.errors;
      enqueueSnackbar(errors?.length ? `${t('presentations.word.editor.apply_refused')} ${errors.join('; ')}` : withReason(t('presentations.word.editor.apply_failed'), error), { variant: 'error' });
    }
  };

  // ---- The document ----

  const renderText = (part: string, index: number) => (
    <Box key={`${part}-${index}`} component="p" data-part={part} data-paragraph={index} sx={{ m: 0, minHeight: '1.6em', typography: 'body2', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {segments(preview.texts.get(`${part}:${index}`) ?? '').map((piece, i) => (piece.placeholder ? (
        <Box key={`${i}-${piece.text}`} component="span" sx={{ px: 0.5, borderRadius: 0.5, bgcolor: 'primary.lighter', color: 'primary.darker', fontFamily: 'monospace', fontSize: '0.92em' }}>{piece.text}</Box>
      ) : piece.text))}
    </Box>
  );

  const rowRepeat = (table: number, row: number) => changes.repeats.findIndex((r) => r.table === table && r.row === row);
  const rangeRepeat = (index: number | undefined) => (index === undefined ? -1 : changes.repeats.findIndex((r) => r.table === undefined && index >= (r.from ?? 0) && index <= (r.to ?? -1)));

  const renderPart = (part: WordLayoutPart) => {
    const blocks: { repeat: number; items: JSX.Element[] }[] = [];
    part.items.forEach((item) => {
      const first = item.type === 'paragraph' ? item.index : item.rows[0]?.cells.flat()[0]?.index;
      const repeat = part.kind === 'body' ? rangeRepeat(first) : -1;
      const element = item.type === 'paragraph' ? renderText(part.part, item.index) : (
        <Box key={`${part.part}-t${item.index}`} component="table" sx={{ width: '100%', borderCollapse: 'collapse', my: 1, '& td.cell': { border: (theme) => `1px solid ${theme.palette.divider}`, p: 0.75, verticalAlign: 'top' } }}>
          <tbody>
            {item.rows.map((row) => {
              const r = part.kind === 'body' ? rowRepeat(item.index, row.index) : -1;
              const canRepeat = part.kind === 'body' && r < 0 && repeat < 0;
              return (
                <Box component="tr" key={row.index} sx={r >= 0 ? { bgcolor: 'primary.lighter' } : undefined}>
                  {row.cells.map((cell, c) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={c} className="cell">{cell.map((p) => renderText(part.part, p.index))}</td>
                  ))}
                  <Box component="td" sx={{ width: 1, pl: 0.5, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {r >= 0 && <Chip size="small" color="primary" icon={<Iconify icon="solar:repeat-bold" width={16} />} label={t(`presentations.word.editor.each.${changes.repeats[r].list}`)} onDelete={() => removeRepeat(r)} />}
                    {canRepeat && (
                      <Tooltip title={t('presentations.word.editor.repeat_row')}>
                        <IconButton size="small" aria-label={t('presentations.word.editor.repeat_row')} onClick={(event) => setRowMenu({ anchor: event.currentTarget, table: item.index, row: row.index })}>
                          <Iconify icon="solar:repeat-linear" width={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </tbody>
        </Box>
      );
      const last = blocks[blocks.length - 1];
      if (last && last.repeat === repeat && repeat >= 0) last.items.push(element);
      else blocks.push({ repeat, items: [element] });
    });

    return blocks.map((block, b) => (block.repeat < 0 ? block.items : (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${part.part}-r${b}`} sx={{ my: 1, pl: 1.5, borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`, bgcolor: 'primary.lighter', borderRadius: 0.5 }}>
        <Chip size="small" color="primary" icon={<Iconify icon="solar:repeat-bold" width={16} />} label={t(`presentations.word.editor.repeats.${changes.repeats[block.repeat].list}`)} onDelete={() => removeRepeat(block.repeat)} sx={{ my: 0.75 }} />
        {block.items}
      </Box>
    )));
  };

  const ordered = [...(parts ?? [])].sort((a, b) => ['header', 'body', 'footer'].indexOf(a.kind) - ['header', 'body', 'footer'].indexOf(b.kind));

  // ---- The placeholder list ----

  const matches = (path: string) => !search.trim() || `${label(path)} ${path}`.toLowerCase().includes(search.trim().toLowerCase());
  const pickerGroups: { title: string; paths: string[] }[] = [
    ...(pickerRepeat >= 0 ? [{ title: t(`presentations.word.editor.each.${changes.repeats[pickerRepeat].list}`), paths: options.lists.get(changes.repeats[pickerRepeat].list) ?? [] }] : []),
    ...options.groups.map(([group, paths]) => ({ title: t(`presentations.word.editor.groups.${group}`), paths })),
  ].map((group) => ({ ...group, paths: group.paths.filter(matches) })).filter((group) => group.paths.length);
  const canRepeatParagraph = picker?.kind === 'text' && picker.part === 'body' && where.get(picker.index)?.table === undefined && pickerRepeat < 0;
  const hasChanges = changes.replacements.length > 0 || changes.repeats.length > 0;

  const describeRepeat = (repeat: WordRepeat) => {
    if (repeat.table !== undefined) return t('presentations.word.editor.row_of_table', { row: (repeat.row ?? 0) + 1, table: repeat.table + 1 });
    const text = Array.from(original.entries()).find(([index, value]) => index >= (repeat.from ?? 0) && index <= (repeat.to ?? -1) && value.trim())?.[1] ?? '';
    return t('presentations.word.editor.from_paragraph', { text: text.length > 40 ? `${text.slice(0, 40)}…` : text });
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 1.5, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" noWrap sx={{ flex: 1 }}>{t('presentations.word.editor.title', { name })}</Typography>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={applying} disabled={!hasChanges || !parts} onClick={save}>{t('presentations.word.editor.apply')}</LoadingButton>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, overflow: { xs: 'auto', md: 'hidden' } }}>
        <Box sx={{ flex: '999 1 560px', minWidth: 0, overflow: 'auto', bgcolor: 'background.neutral', p: { xs: 2, md: 3 } }}>
          <Alert severity="info" sx={{ mb: 2, maxWidth: 860, mx: 'auto' }}>{t('presentations.word.editor.intro')}</Alert>
          {(loading || (!parts && !failed)) && <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>}
          {failed && <Alert severity="error" sx={{ maxWidth: 860, mx: 'auto' }}>{t('presentations.word.editor.load_failed')}</Alert>}
          {parts && (
            <Box ref={page} onMouseUp={onMouseUp} sx={{ maxWidth: 860, mx: 'auto', bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1, p: { xs: 2, md: 5 }, cursor: 'text' }}>
              {ordered.map((part) => (
                <Box key={part.part} sx={part.kind === 'body' ? undefined : { my: 2, p: 1.5, border: (theme) => `1px dashed ${theme.palette.divider}`, borderRadius: 1 }}>
                  {part.kind !== 'body' && <Typography variant="overline" sx={{ color: 'text.disabled' }}>{t(`presentations.word.editor.${part.kind}`)}</Typography>}
                  {renderPart(part)}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box component="aside" aria-label={t('presentations.word.editor.changes')} sx={{ flex: '1 1 320px', maxWidth: { md: 380 }, minWidth: 0, overflow: 'auto', p: 2.5, borderLeft: (theme) => `1px solid ${theme.palette.divider}` }}>
          {aiAvailable && (
            <Stack spacing={0.75} sx={{ mb: 2.5 }}>
              <LoadingButton variant="soft" color="secondary" loading={suggesting} disabled={!parts} onClick={suggestWithAi} startIcon={<Iconify icon="solar:magic-stick-3-bold" />}>
                {t('presentations.word.ai_suggest')}
              </LoadingButton>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.word.ai_privacy')}</Typography>
              {notes.map((note) => <Alert key={note} severity="info" icon={<Iconify icon="solar:repeat-bold" />}>{note}</Alert>)}
            </Stack>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('presentations.word.editor.changes')}</Typography>
          {!hasChanges && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('presentations.word.editor.none')}</Typography>}
          <Stack spacing={1}>
            {changes.replacements.map((change, i) => (
              <ChangeRow key={`g-${change.find}`} find={change.find} label={label(change.placeholder)} count={preview.counts.get(`g${i}`) ?? 0} onRemove={() => removeReplacement(-1, i)} />
            ))}
            {changes.repeats.map((repeat, r) => (
              <Box key={`r-${repeat.list}-${repeat.table ?? ''}-${repeat.row ?? ''}-${repeat.from ?? ''}`} sx={{ p: 1, borderRadius: 1, border: (theme) => `1px dashed ${theme.palette.primary.main}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify icon="solar:repeat-bold" width={18} sx={{ color: 'primary.main' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2">{t(`presentations.word.editor.repeats.${repeat.list}`)}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="p">{describeRepeat(repeat)}</Typography>
                  </Box>
                  <IconButton size="small" aria-label={t('presentations.word.editor.stop_repeating')} onClick={() => removeRepeat(r)}><Iconify icon="mingcute:close-line" width={16} /></IconButton>
                </Stack>
                <Stack spacing={0.75} sx={{ mt: 1, pl: 3.5 }}>
                  {!repeat.replacements.length && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.word.editor.repeat_empty')}</Typography>}
                  {repeat.replacements.map((change, i) => (
                    <ChangeRow key={`r${r}-${change.find}`} find={change.find} label={label(change.placeholder)} count={preview.counts.get(`r${r}.${i}`) ?? 0} onRemove={() => removeReplacement(r, i)} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Popover
        open={picker?.kind === 'text'}
        onClose={() => setPicker(null)}
        anchorReference="anchorPosition"
        anchorPosition={picker ? { top: picker.top, left: picker.left } : undefined}
      >
        {picker?.kind === 'text' && (
          <Box sx={{ width: 340, maxHeight: 440, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5, pb: 1 }}>
              <Typography variant="subtitle2" noWrap>{t('presentations.word.editor.replace_with', { text: picker.find })}</Typography>
              <TextField autoFocus fullWidth size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('presentations.word.editor.search')} inputProps={{ 'aria-label': t('presentations.word.editor.search') }} sx={{ mt: 1 }} />
            </Box>
            <MenuList dense sx={{ overflow: 'auto', py: 0 }}>
              {pickerGroups.flatMap((group) => [
                <ListSubheader key={`h-${group.title}`} sx={{ lineHeight: '32px' }}>{group.title}</ListSubheader>,
                ...group.paths.map((path) => (
                  <MenuItem key={path} onClick={() => choose(path)}>
                    <ListItemText primary={label(path)} secondary={placeholderText(path, pickerRepeat >= 0 ? changes.repeats[pickerRepeat] : undefined)} secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </MenuItem>
                )),
              ])}
            </MenuList>
            {canRepeatParagraph && (
              <>
                <Divider />
                <MenuItem onClick={() => setPicker({ kind: 'paragraphs', from: picker.index, to: picker.index, top: picker.top, left: picker.left })}>
                  <Iconify icon="solar:repeat-linear" width={18} sx={{ mr: 1 }} />
                  {t('presentations.word.editor.repeat_paragraph')}
                </MenuItem>
              </>
            )}
          </Box>
        )}
      </Popover>

      <Popover
        open={picker?.kind === 'paragraphs'}
        onClose={() => setPicker(null)}
        anchorReference="anchorPosition"
        anchorPosition={picker ? { top: picker.top, left: picker.left } : undefined}
      >
        <MenuList dense sx={{ minWidth: 240 }}>
          <ListSubheader sx={{ lineHeight: '32px' }}>{t('presentations.word.editor.repeat_paragraphs')}</ListSubheader>
          {Array.from(options.lists.keys()).map((list) => (
            <MenuItem key={list} onClick={() => picker?.kind === 'paragraphs' && addRepeat({ list, from: picker.from, to: picker.to, replacements: [] })}>
              {t(`presentations.word.editor.each.${list}`)}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>

      <Menu open={!!rowMenu} anchorEl={rowMenu?.anchor} onClose={() => setRowMenu(null)}>
        <ListSubheader sx={{ lineHeight: '32px' }}>{t('presentations.word.editor.repeat_row_for')}</ListSubheader>
        {Array.from(options.lists.keys()).map((list) => (
          <MenuItem key={list} onClick={() => rowMenu && addRepeat({ list, table: rowMenu.table, row: rowMenu.row, replacements: [] })}>
            {t(`presentations.word.editor.each.${list}`)}
          </MenuItem>
        ))}
      </Menu>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

function ChangeRow({ find, label, count, onRemove }: { find: string; label: string; count: number; onRemove: VoidFunction }) {
  const { t } = useTranslate();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={find}>&ldquo;{find}&rdquo;</Typography>
        <Typography variant="caption" component="p" sx={{ color: count ? 'text.secondary' : 'warning.main' }}>
          → {label} · {count ? t('presentations.word.editor.places', { count }) : t('presentations.word.editor.not_found')}
        </Typography>
      </Box>
      <IconButton size="small" aria-label={t('presentations.word.editor.remove', { text: find })} onClick={onRemove}><Iconify icon="mingcute:close-line" width={16} /></IconButton>
    </Stack>
  );
}
